import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Star, Pencil } from 'lucide-react';
import { apiClient } from '@/lib/apiClient';
import type { Plan } from '@/_types';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';

async function fetchPlans(page: number) {
  const res = await apiClient.get('/plans', { params: { page, per_page: 15 } });
  return res.data;
}

export default function PlansPage() {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);

  const { data, isLoading } = useQuery({
    queryKey: ['admin-plans', page],
    queryFn: () => fetchPlans(page),
  });

  const plans: Plan[] = data?.data?.plans ?? [];
  const meta = data?.meta;

  const formatPrice = (dollars: number) =>
    `$${dollars.toLocaleString('en-US')}`;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold">Plans</h1>
        <p className="text-sm text-muted-foreground">Manage pricing plans</p>
      </div>

      <div className="rounded-xl border bg-card overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-muted/50">
            <tr>
              <th className="px-4 py-3 text-left font-medium text-muted-foreground">Name</th>
              <th className="px-4 py-3 text-left font-medium text-muted-foreground">Slug</th>
              <th className="px-4 py-3 text-left font-medium text-muted-foreground">Monthly</th>
              <th className="px-4 py-3 text-left font-medium text-muted-foreground">Annual</th>
              <th className="px-4 py-3 text-left font-medium text-muted-foreground">Trial</th>
              <th className="px-4 py-3 text-left font-medium text-muted-foreground">Status</th>
              <th className="px-4 py-3 text-right font-medium text-muted-foreground">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {isLoading ? (
              <tr>
                <td colSpan={7} className="px-4 py-8 text-center text-muted-foreground">Loading…</td>
              </tr>
            ) : plans.length === 0 ? (
              <tr>
                <td colSpan={7} className="px-4 py-8 text-center text-muted-foreground">No plans found</td>
              </tr>
            ) : (
              plans.map((plan) => (
                <tr key={plan.id} className="hover:bg-muted/20 transition-colors">
                  <td className="px-4 py-3 font-medium">
                    <div className="flex items-center gap-1.5">
                      {plan.name}
                      {plan.is_popular && (
                        <Star className="h-3.5 w-3.5 fill-amber-400 text-amber-400" />
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground">{plan.tagline}</p>
                  </td>
                  <td className="px-4 py-3 font-mono text-xs text-muted-foreground">{plan.slug}</td>
                  <td className="px-4 py-3">{formatPrice(plan.monthly_price)}/mo</td>
                  <td className="px-4 py-3">{formatPrice(plan.annual_price)}/mo billed yearly</td>
                  <td className="px-4 py-3">{plan.trial_days}d</td>
                  <td className="px-4 py-3">
                    <Badge variant={plan.is_active ? 'success' : 'secondary'}>
                      {plan.is_active ? 'Active' : 'Inactive'}
                    </Badge>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <Button variant="ghost" size="sm" onClick={() => navigate(`/plans/${plan.id}/edit`)}>
                      <Pencil className="h-4 w-4 mr-1" /> Edit
                    </Button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {meta && meta.last_page > 1 && (
        <div className="flex items-center justify-between text-sm text-muted-foreground">
          <span>Page {meta.current_page} of {meta.last_page}</span>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" disabled={page === 1} onClick={() => setPage((p) => p - 1)}>
              Previous
            </Button>
            <Button variant="outline" size="sm" disabled={!meta.has_more} onClick={() => setPage((p) => p + 1)}>
              Next
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
